import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

function NAVValidationDashboard({ user }) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validations, setValidations] = useState([]);
  const [brandMappings, setBrandMappings] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [brandMappingModal, setBrandMappingModal] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [newAlias, setNewAlias] = useState('');

  useEffect(() => {
    loadValidations();
    loadBrandMappings();
  }, []);

  const loadValidations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/erp_validation.php?action=get_validations`, {
        withCredentials: true
      });
      if (response.data.success) {
        setValidations(response.data.validations || []);
      }
    } catch (error) {
      console.error('Error loading validations:', error);
    }
  };

  const loadBrandMappings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/erp_validation.php?action=get_brand_mappings`, {
        withCredentials: true
      });
      if (response.data.success) {
        setBrandMappings(response.data.mappings || []);
      }
    } catch (error) {
      console.error('Error loading brand mappings:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/erp_validation.php?action=download_template`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'NAV_Stock_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template');
    }
  };

  const handleUploadAndValidate = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    if (!selectedDate) {
      alert('Please select a date');
      return;
    }

    setUploading(true);
    setValidationResult(null);
    
    const blob = selectedFile.slice(0, selectedFile.size, selectedFile.type);
    const newFile = new File([blob], selectedFile.name, { type: selectedFile.type });
    
    const formData = new FormData();
    formData.append('file', newFile);
    formData.append('selected_date', selectedDate);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/erp_validation.php`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('Upload response:', response.data);

      if (response.data.success) {
        setValidationResult(response.data);
        alert('Success: ' + response.data.message);
        loadValidations();
      } else {
        alert('Validation failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  // ✅ Export validation results
  const handleExportResults = async () => {
    if (!validationResult) {
      alert('No validation data to export');
      return;
    }

    setExporting(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/erp_validation.php?action=export_validation_results`,
        validationResult,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      // Create blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NAV_Validation_Report_${selectedDate || new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      alert('Export successful!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setExporting(false);
    }
  };

  const handleAddBrandMapping = async () => {
    if (!newBrandName || !newAlias) {
      alert('Please enter both brand name and alias');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/erp_validation.php?action=add_brand_mapping`,
        { brand_name: newBrandName, alias: newAlias },
        { withCredentials: true }
      );

      if (response.data.success) {
        loadBrandMappings();
        setNewBrandName('');
        setNewAlias('');
        setBrandMappingModal(false);
      }
    } catch (error) {
      console.error('Error adding brand mapping:', error);
    }
  };

  const handleDeleteBrandMapping = async (id) => {
    if (!window.confirm('Delete this brand mapping?')) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/erp_validation.php?action=delete_brand_mapping`,
        { id },
        { withCredentials: true }
      );

      if (response.data.success) {
        loadBrandMappings();
      }
    } catch (error) {
      console.error('Error deleting brand mapping:', error);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(num || 0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          📊 NAV Stock Validation Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Compare NAV stock data with system inventory records
        </Typography>
      </Box>

      {/* Upload Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Upload & Validate
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          {/* Date Selector */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Coldstore Data As On"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ 
                shrink: true,
                sx: { fontWeight: 600 }
              }}
              inputProps={{
                max: new Date().toISOString().split('T')[0]
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontWeight: 600
                }
              }}
            />
          </Grid>

          {/* File Upload */}
          <Grid item xs={12} md={4}>
            <input
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              id="nav-file-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="nav-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ 
                  fontWeight: 700,
                  height: '56px'
                }}
              >
                SELECT NAV EXCEL FILE
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1, color: 'success.main', fontWeight: 600 }}>
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Grid>

          {/* Download Template Button */}
          <Grid item xs={12} md={2.5}>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              fullWidth
              sx={{ 
                fontWeight: 700,
                height: '56px'
              }}
            >
              Download Template
            </Button>
          </Grid>

          {/* Validate Button */}
          <Grid item xs={12} md={2.5}>
            <Button
              variant="contained"
              color="primary"
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleUploadAndValidate}
              disabled={uploading || !selectedFile || !selectedDate}
              fullWidth
              sx={{ 
                fontWeight: 700,
                height: '56px'
              }}
            >
              {uploading ? 'VALIDATING...' : 'VALIDATE'}
            </Button>
          </Grid>
        </Grid>

        {/* Date Selection Info */}
        {selectedDate && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>
              Validating stock data up to: <strong>{new Date(selectedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
            </Typography>
          </Alert>
        )}

        {/* Info Message */}
        <Alert severity="info" icon={<WarningIcon />} sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Excel Format: NAV Export - Columns: PRODUCT | SIZE (Grade) | PACK (Pack Size) | BRAND | WHS QTY. In CAR
          </Typography>
        </Alert>
      </Paper>

      {/* Brand Mappings Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<LinkIcon />}
          onClick={() => setBrandMappingModal(true)}
          sx={{ fontWeight: 700 }}
        >
          BRAND MAPPINGS
        </Button>
      </Box>

      {/* Results Section */}
      {validationResult && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Validation Results
            </Typography>
            {/* ✅ EXPORT BUTTON */}
            <Button
              variant="contained"
              color="success"
              startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
              onClick={handleExportResults}
              disabled={exporting}
              sx={{ fontWeight: 700 }}
            >
              {exporting ? 'EXPORTING...' : '📥 EXPORT TO EXCEL'}
            </Button>
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={2.4}>
              <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        PERFECT MATCHES
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 900 }}>
                        {validationResult.summary.perfect_matches}
                      </Typography>
                    </Box>
                    <CheckCircleIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        QTY DIFFERENCES
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 900 }}>
                        {validationResult.summary.qty_differences}
                      </Typography>
                    </Box>
                    <WarningIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: 'error.main', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        MISSING IN SYSTEM
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 900 }}>
                        {validationResult.summary.missing_in_system}
                      </Typography>
                    </Box>
                    <ErrorIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: '#9c27b0', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        MISSING IN NAV
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 900 }}>
                        {validationResult.summary.missing_in_nav}
                      </Typography>
                    </Box>
                    <ErrorIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: '#ff9800', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        POSSIBLE MATCHES
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 900 }}>
                        {validationResult.summary.possible_matches || 0}
                      </Typography>
                    </Box>
                    <LinkIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Total Quantities */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
              Total Quantities
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                  <CardContent>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>NAV TOTAL CARTONS</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 900 }}>
                      {formatNumber(validationResult.summary.total_nav_qty)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: 'secondary.main', color: 'white' }}>
                  <CardContent>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>SYSTEM TOTAL CARTONS</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 900 }}>
                      {formatNumber(validationResult.summary.total_system_qty)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  bgcolor: validationResult.summary.total_qty_difference >= 0 ? 'success.main' : 'error.main', 
                  color: 'white' 
                }}>
                  <CardContent>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>DIFFERENCE</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 900 }}>
                      {validationResult.summary.total_qty_difference >= 0 ? '+' : ''}
                      {formatNumber(validationResult.summary.total_qty_difference)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          {/* Date Info */}
          {validationResult.selected_date && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700 }}>
                Validation completed for data as on: <strong>{new Date(validationResult.selected_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
              </Typography>
            </Alert>
          )}

          {/* Tabs for different views */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
              <Tab 
                label={`Perfect Matches (${validationResult.summary.perfect_matches})`}
                sx={{ fontWeight: 700 }}
              />
              <Tab 
                label={`Qty Differences (${validationResult.summary.qty_differences})`}
                sx={{ fontWeight: 700 }}
              />
              <Tab 
                label={`Missing in System (${validationResult.summary.missing_in_system})`}
                sx={{ fontWeight: 700 }}
              />
              <Tab 
                label={`Missing in NAV (${validationResult.summary.missing_in_nav})`}
                sx={{ fontWeight: 700 }}
              />
              <Tab 
                label={`Possible Matches (${validationResult.summary.possible_matches || 0})`}
                sx={{ fontWeight: 700 }}
              />
            </Tabs>
          </Box>

          {/* Perfect Matches */}
          {activeTab === 0 && (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#4caf50', color: 'white' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#4caf50', color: 'white' }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#4caf50', color: 'white' }}>Pack Size</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#4caf50', color: 'white' }}>Brand</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#4caf50', color: 'white' }} align="right">Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validationResult.details.perfect_matches.map((item, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{item.product}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.grade}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.pack_size}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.brand}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(item.system_qty)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Quantity Differences */}
          {activeTab === 1 && (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>Pack Size</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>Brand</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }} align="right">System Qty</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }} align="right">NAV Qty</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }} align="right">Difference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validationResult.details.qty_differences.map((item, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{item.product}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.grade}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.pack_size}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.brand}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(item.system_qty)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(item.nav_qty)}</TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 900,
                        color: item.difference > 0 ? 'success.main' : 'error.main'
                      }}>
                        {item.difference > 0 ? '+' : ''}{formatNumber(item.difference)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Missing in System */}
          {activeTab === 2 && (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#f44336', color: 'white' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#f44336', color: 'white' }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#f44336', color: 'white' }}>Pack Size</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#f44336', color: 'white' }}>Brand</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#f44336', color: 'white' }} align="right">NAV Qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validationResult.details.missing_in_system.map((item, idx) => (
                    <TableRow key={idx} hover sx={{ bgcolor: '#ffebee' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{item.product}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.grade}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.pack_size}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.brand}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(item.nav_qty)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Missing in NAV */}
          {activeTab === 3 && (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#9c27b0', color: 'white' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#9c27b0', color: 'white' }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#9c27b0', color: 'white' }}>Pack Size</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#9c27b0', color: 'white' }}>Brand</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#9c27b0', color: 'white' }} align="right">System Qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validationResult.details.missing_in_nav.map((item, idx) => (
                    <TableRow key={idx} hover sx={{ bgcolor: '#f3e5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{item.product}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.grade}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.pack_size}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.brand}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(item.system_qty)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Possible Matches */}
          {activeTab === 4 && (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>Scenario</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>Pack Size</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>System Brand</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>NAV Brand</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }} align="right">System Qty</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }} align="right">NAV Qty</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }} align="right">Difference</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>Confidence</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: '#ff9800', color: 'white' }}>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(validationResult.details.possible_matches || []).map((item, idx) => (
                    <TableRow key={idx} hover sx={{ bgcolor: item.confidence === 'High' ? '#fff8e1' : '#f3e5f5' }}>
                      <TableCell>
                        <Chip 
                          label={item.scenario} 
                          color={
                            item.scenario === 'Scenario 1' ? 'success' : 
                            item.scenario === 'Scenario 2' ? 'warning' : 
                            'info'
                          }
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.product}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.grade}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.pack_size}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>{item.system_brand}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'secondary.main' }}>{item.nav_brand}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(item.system_qty)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(item.nav_qty)}</TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 900,
                        color: item.qty_difference > 0 ? 'success.main' : 'error.main'
                      }}>
                        {item.qty_difference > 0 ? '+' : ''}{formatNumber(item.qty_difference)}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.confidence} 
                          color={item.confidence === 'High' ? 'success' : 'warning'} 
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{item.reason}</TableCell>
                    </TableRow>
                  ))}
                  {(!validationResult.details.possible_matches || validationResult.details.possible_matches.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          No possible matches found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Brand Mapping Modal */}
      <Dialog open={brandMappingModal} onClose={() => setBrandMappingModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Brand Mappings</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={5}>
              <TextField
                fullWidth
                label="NAV Brand"
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                InputLabelProps={{ sx: { fontWeight: 600 } }}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                fullWidth
                label="System Brand"
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                InputLabelProps={{ sx: { fontWeight: 600 } }}
              />
            </Grid>
            <Grid item xs={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAddBrandMapping}
                sx={{ height: '56px', fontWeight: 700 }}
              >
                Add
              </Button>
            </Grid>
          </Grid>

          <TableContainer sx={{ mt: 3, maxHeight: 400 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1565c0' }}>
                  <TableCell sx={{ fontWeight: 900, color: 'white' }}>NAV Brand</TableCell>
                  <TableCell sx={{ fontWeight: 900, color: 'white' }}>System Brand</TableCell>
                  <TableCell sx={{ fontWeight: 900, color: 'white' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brandMappings.map((mapping) => (
                  <TableRow key={mapping.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{mapping.brand_name}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{mapping.alias}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteBrandMapping(mapping.id)}
                      >
                        <CloseIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrandMappingModal(false)} sx={{ fontWeight: 700 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default NAVValidationDashboard;